import React from 'react';
import PropTypes from 'prop-types';
import {
  ApolloClient, InMemoryCache, from, ApolloLink, ApolloProvider,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { useAuth } from 'reactfire';
import { datadogRum } from '@datadog/browser-rum';
import trackError from 'shared/helpers/errorTracking';
import { setContext } from '@apollo/client/link/context';

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    const criticalErrors = [
      'Error decoding signature',
      'Signature has expired',
      'User is disabled',
      'Unauthorized',
    ];
    graphQLErrors.forEach(({ message }) => {
      if (criticalErrors.includes(message)) {
        window.localStorage.clear();
        window.localStorage.setItem('isLogin', true);
        const locationParams = `${window.location.pathname}${window.location.search}`;
        if (!window.location.href.includes('/account/sign-in/')) {
          window.location.href = `/account/sign-in/?next=${locationParams}`;
        }
      } else {
        trackError(new Error(message));
      }
    });
  }
  if (networkError) {
    trackError(networkError);
  }
});

const monitoringResponseLoggerLink = new ApolloLink((operation, forward) => (
  forward(operation).map(response => {
    const logData = {
      response: response.data,
      operationName: operation.operationName,
      variables: operation.variables,
    };
    // Datadog: custom action for GraphQL responses
    try {
      if (typeof datadogRum?.getInternalContext === 'function' && datadogRum.getInternalContext()) {
        datadogRum.addAction(`GraphQL ${operation.operationName} Response`, logData);
      }
    } catch (_) {
      // ignore
    }
    return response;
  })
));

const uploadLink = createUploadLink({ uri: process.env.REACT_APP_APOLLO_CLIENT_URI });

const AuthApolloProvider = ({ children }) => {
  const auth = useAuth();

  const client = new ApolloClient({
    link: from([
      setContext(async (_, { headers }) => {
        if (!auth.currentUser) {
          return { headers };
        }
        const authToken = await auth.currentUser?.getIdToken();
        return {
          headers: {
            ...headers,
            authorization: authToken,
          },
        };
      }),
      monitoringResponseLoggerLink,
      errorLink,
      uploadLink,
    ]),
    cache: new InMemoryCache(),
  });

  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
};

AuthApolloProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthApolloProvider;
